/**
 * Lightweight per-reaction celebratory animations.
 * Heart  — pop + spin + heart-burst particles
 * Fire   — pop + flicker/wobble + glow
 * Diamond — pop + spin + sparkle-burst particles
 *
 * Uses CSS class toggles + short-lived DOM overlays.
 * Respects prefers-reduced-motion.
 */

const PARTICLE_COUNT = 8;
const BURST_DURATION = 520; // ms — matches CSS animation

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── CSS class animation on the SVG icon ─────────────────────── */

function animateIcon(btn, type) {
  const svg = btn.querySelector('svg');
  if (!svg) return;

  const cls = `react-${type}-anim`;
  svg.classList.remove('react-pop', cls);
  void svg.offsetWidth;           // reflow to restart
  svg.classList.add('react-pop', cls);

  const cleanup = () => {
    svg.classList.remove(cls);
    svg.removeEventListener('animationend', cleanup);
  };
  svg.addEventListener('animationend', cleanup, { once: true });
  // safety fallback
  setTimeout(() => svg.classList.remove(cls), 700);
}

/* ── Particle burst overlay (heart & diamond only) ───────────── */

function injectBurst(btn, type) {
  // remove any leftover burst
  btn.querySelectorAll('.react-burst').forEach(el => el.remove());

  const overlay = document.createElement('span');
  overlay.className = `react-burst react-burst-${type}`;
  overlay.setAttribute('aria-hidden', 'true');

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const p = document.createElement('span');
    p.className = 'react-particle';
    // spread particles evenly around the circle
    const angle = (360 / PARTICLE_COUNT) * i;
    p.style.setProperty('--angle', `${angle}deg`);
    overlay.appendChild(p);
  }

  btn.style.position = 'relative';
  btn.style.overflow = 'visible';
  btn.appendChild(overlay);

  setTimeout(() => overlay.remove(), BURST_DURATION + 80);
}

/* ── Public API ───────────────────────────────────────────────── */

/**
 * Play the celebratory animation for a reaction button.
 * Call this ONLY on "added" or "updated" actions (not on remove).
 *
 * @param {HTMLElement} btn  — the <button> element
 * @param {string}      type — 'heart' | 'fire' | 'diamond'
 */
export function playReactionAnimation(btn, type) {
  if (!btn || prefersReducedMotion()) return;

  animateIcon(btn, type);

  if (type === 'heart' || type === 'diamond') {
    injectBurst(btn, type);
  }
}
